import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Shell from "@/components/Shell";
import StatusBadge from "@/components/StatusBadge";
import StatusTimeline from "@/components/StatusTimeline";
import { api, formatBytes, formatDate, formatApiErrorDetail, STATUS_LABELS, STATUS_ORDER } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Download, FileText, CheckCircle2, Send, Gavel } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const ADMIN_CAT_LABEL = {
  POLICY: "Policy",
  BILL: "Bill",
  MEDICAL: "Medical",
  KYC: "KYC",
  CLAIM_FORM: "Claim Form",
  OTHER: "Other",
};

export default function AdminCaseDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [docs, setDocs] = useState([]);
  const [tab, setTab] = useState("status");

  const [statusForm, setStatusForm] = useState({ status: "UNDER_REVIEW", note: "" });
  const [opinionForm, setOpinionForm] = useState({ opinion: "", recommendation: "" });
  const [updateForm, setUpdateForm] = useState({ message: "" });
  const [busy, setBusy] = useState(false);

  const loadCase = async () => {
    try {
      const { data } = await api.get(`/cases/${id}`);
      setC(data);
      setStatusForm({ status: data.status, note: "" });
      setOpinionForm({ opinion: data.opinion || "", recommendation: data.recommendation || "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load case");
      nav("/admin");
    }
  };
  const loadDocs = async () => {
    try {
      const { data } = await api.get(`/cases/${id}/documents`);
      setDocs(data || []);
    } catch (_) {}
  };
  useEffect(() => { loadCase(); loadDocs(); }, [id]);

  const onStatusSubmit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.patch(`/cases/${id}/status`, statusForm);
      setC(data); toast.success(`Status updated to ${STATUS_LABELS[data.status]}`); setStatusForm({ ...statusForm, note: "" });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed"); } finally { setBusy(false); }
  };
  const onOpinionSubmit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post(`/cases/${id}/opinion`, opinionForm);
      setC(data); toast.success("Expert opinion saved & client notified");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Submit failed"); } finally { setBusy(false); }
  };
  const onUpdateSubmit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post(`/cases/${id}/update-client`, updateForm);
      setC(data); setUpdateForm({ message: "" }); toast.success("Update sent to client");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed"); } finally { setBusy(false); }
  };
  const onProcessed = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/cases/${id}/process`); setC(data); toast.success("Case marked processed"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); } finally { setBusy(false); }
  };

  if (!c) return <Shell><div className="px-6 py-10 text-sm text-slate-500">Loading…</div></Shell>;

  return (
    <Shell>
      <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-8">
        <button data-testid="admin-case-back-btn" onClick={() => nav("/admin")} className="text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-emerald-brand font-semibold flex items-center gap-2 mb-6">
          <ArrowLeft className="w-3 h-3" /> Inbox
        </button>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="text-xs font-mono text-emerald-brand font-semibold" data-testid="admin-case-no">{c.case_no}</div>
            <h1 className="font-heading font-bold text-3xl md:text-4xl mt-1 leading-tight" data-testid="admin-case-title">{c.title}</h1>
            <div className="mt-2 text-xs text-slate-600">
              <span className="font-semibold">{c.client_name}</span> · {c.client_email} · {c.category}
            </div>
            <p className="mt-3 text-sm text-slate-600 max-w-2xl">{c.description || "No description provided."}</p>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge status={c.status} />
            {c.status !== "PROCESSED" && (
              <button onClick={onProcessed} disabled={busy} data-testid="admin-mark-processed-btn" className="btn-emerald !py-2 !px-4 text-xs">
                <CheckCircle2 className="w-4 h-4" /> Mark processed
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* LEFT: Documents */}
          <section className="lg:col-span-5 brand-card p-0 overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Client documents</span>
              <span className="text-xs text-slate-500">{docs.length} files</span>
            </div>
            {docs.length === 0 ? (
              <div className="p-10 text-center text-xs uppercase tracking-wider text-slate-400">
                Client has not uploaded documents yet
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {docs.map((d) => (
                  <li key={d.id} data-testid={`admin-doc-${d.id}`} className="px-5 py-3 flex items-center justify-between gap-4 hover:bg-slate-50">
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="w-4 h-4 text-emerald-brand shrink-0" />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm font-medium truncate">{d.filename}</span>
                          <span data-testid={`admin-doc-category-${d.id}`} className="inline-flex shrink-0 items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold bg-slate-100 text-slate-700">
                            {ADMIN_CAT_LABEL[d.category] || "Other"}
                          </span>
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">{formatBytes(d.size)} · {formatDate(d.uploaded_at)}</div>
                      </div>
                    </div>
                    <a href={`${api.defaults.baseURL}/documents/${d.id}/download`} target="_blank" rel="noreferrer"
                       data-testid={`admin-doc-download-${d.id}`}
                       className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 hover:border-navy hover:bg-navy hover:text-white transition-colors text-xs font-semibold">
                      <Download className="w-3 h-3" /> Download
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* CENTER: Tabs */}
          <section className="lg:col-span-4 brand-card p-0 overflow-hidden">
            <div className="border-b border-slate-100 grid grid-cols-3">
              {[{k:"status",label:"Status"},{k:"opinion",label:"Opinion"},{k:"update",label:"Update"}].map((t) => (
                <button key={t.k} data-testid={`admin-tab-${t.k}`} onClick={() => setTab(t.k)}
                  className={`py-3 text-xs uppercase tracking-wider font-semibold transition-colors ${tab === t.k ? "bg-navy text-white" : "hover:bg-slate-50 text-slate-600"}`}>
                  {t.label}
                </button>
              ))}
            </div>

            {tab === "status" && (
              <form onSubmit={onStatusSubmit} className="p-5 space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Move case to</Label>
                  <Select value={statusForm.status} onValueChange={(v) => setStatusForm({ ...statusForm, status: v })}>
                    <SelectTrigger data-testid="admin-status-select" className="mt-1.5 rounded-xl border-slate-200 h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      {STATUS_ORDER.map((s) => (<SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Note (sent to client)</Label>
                  <Textarea rows={3} data-testid="admin-status-note-input"
                    value={statusForm.note} onChange={(e) => setStatusForm({ ...statusForm, note: e.target.value })}
                    placeholder="Optional note that will appear in the timeline."
                    className="mt-1.5 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-0" />
                </div>
                <button type="submit" disabled={busy} data-testid="admin-status-submit-btn" className="btn-primary w-full text-xs">
                  Update status
                </button>
              </form>
            )}

            {tab === "opinion" && (
              <form onSubmit={onOpinionSubmit} className="p-5 space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Expert opinion</Label>
                  <Textarea required rows={6} data-testid="admin-opinion-input"
                    value={opinionForm.opinion} onChange={(e) => setOpinionForm({ ...opinionForm, opinion: e.target.value })}
                    placeholder="Detail the expert's findings, references and reasoning."
                    className="mt-1.5 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-0" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Recommendation</Label>
                  <Textarea rows={3} data-testid="admin-recommendation-input"
                    value={opinionForm.recommendation} onChange={(e) => setOpinionForm({ ...opinionForm, recommendation: e.target.value })}
                    placeholder="What should the client do next?"
                    className="mt-1.5 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-0" />
                </div>
                <button type="submit" disabled={busy} data-testid="admin-opinion-submit-btn" className="btn-emerald w-full text-xs">
                  <Gavel className="w-4 h-4" /> Save & notify client
                </button>
              </form>
            )}

            {tab === "update" && (
              <form onSubmit={onUpdateSubmit} className="p-5 space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Send a message to client</Label>
                  <Textarea required rows={6} data-testid="admin-update-message-input"
                    value={updateForm.message} onChange={(e) => setUpdateForm({ message: e.target.value })}
                    placeholder="The client will see this on their case page and receive a notification."
                    className="mt-1.5 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-0" />
                </div>
                <button type="submit" disabled={busy} data-testid="admin-update-submit-btn" className="btn-primary w-full text-xs">
                  <Send className="w-4 h-4" /> Send update
                </button>
              </form>
            )}
          </section>

          {/* RIGHT: Timeline */}
          <section className="lg:col-span-3">
            <StatusTimeline status={c.status} timeline={c.timeline} compact />
          </section>
        </div>

        {(c.opinion || c.client_update) && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            {c.opinion && (
              <div className="brand-card p-6" data-testid="admin-opinion-preview">
                <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-brand font-semibold mb-2">Saved expert opinion</div>
                <p className="text-sm whitespace-pre-wrap leading-relaxed text-slate-700">{c.opinion}</p>
                {c.recommendation && (
                  <>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-brand font-semibold mt-5 mb-2">Recommendation</div>
                    <p className="text-sm whitespace-pre-wrap leading-relaxed text-slate-700">{c.recommendation}</p>
                  </>
                )}
              </div>
            )}
            {c.client_update && (
              <div className="brand-card p-6" data-testid="admin-update-preview">
                <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-2">Latest client update</div>
                <p className="text-sm whitespace-pre-wrap leading-relaxed text-slate-700">{c.client_update}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </Shell>
  );
}
